import type { ReactNode } from "react";
import type { TaskPriority } from "@sts/schemas";
import { PRIORITY_LABEL, PRIORITY_MARK } from "../lib/tasks.js";

/**
 * How urgent a task is, said three ways at once.
 *
 * The class carries the colour, the word carries the meaning, and the mark gives
 * the eye a shape to scan down a column. The word and the mark are the load-
 * bearing pair: this board is printed in black and white for site meetings, and
 * some of the people who read it every day cannot distinguish the amber row from
 * the red one. Everything below still reads correctly with the stylesheet
 * removed, which is the test worth applying to a status indicator.
 *
 * The mark is hidden from assistive technology on purpose. It is a redundant
 * encoding of the word beside it, so announcing it would only make a screen
 * reader say the same thing twice, the second time as punctuation.
 */
export function PriorityBadge({
  priority,
}: {
  readonly priority: TaskPriority;
}): ReactNode {
  return (
    <span className={`status-word priority-${priority}`}>
      <span className="priority-mark" aria-hidden="true">
        {PRIORITY_MARK[priority]}
      </span>{" "}
      {PRIORITY_LABEL[priority]}
    </span>
  );
}

/**
 * Whether a task is late, done, or neither.
 *
 * "Overdue" is a word rather than a red row for the same reason as above, and it
 * is a separate element from the date so that a reader who is scanning states
 * does not have to do date arithmetic to find them.
 */
export function TaskState({
  overdue,
  done,
}: {
  readonly overdue: boolean;
  readonly done: boolean;
}): ReactNode {
  if (done) return <span className="status-word status-tracking">Done</span>;
  if (overdue) return <span className="status-word status-breached">Overdue</span>;
  return <span className="muted">Open</span>;
}
